import { Injectable } from '@angular/core';
import { SegmentsConfigService } from './segments.config.service';
import { SegmentsReportService } from './segments.report.service';
import { State, SegmentsState, getSegmentsOptions, getSegmentsReport } from '../../reducers';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { map, pairwise, filter, shareReplay, startWith, catchError } from 'rxjs/operators';
import * as CoreActions from '@uxdf/direct-shared-lib';

@Injectable({
  providedIn: 'root'
})
export class SegmentsService {
  private segments$: Observable<SegmentsState>;
  private segmentsCombined$: Observable<any>;
  private segmentsLoaded$: Observable<any>;

  constructor(
    private readonly store: Store<State>,
    private readonly configService: SegmentsConfigService,
    private readonly reportService: SegmentsReportService
  ) {}

  public getSegments(): Observable<SegmentsState> {
    if (!this.segments$) {
      this.segments$ = this.getSegmentsCombined().pipe(shareReplay(1));
    }

    return this.segments$;
  }

  public getLoadedSegments(): Observable<SegmentsState> {
    if (!this.segmentsLoaded$) {
      this.segmentsLoaded$ = this.getSegmentsCombined().pipe(
        startWith({ report: { updateRequired: true } } as SegmentsState),
        pairwise(),
        filter(
          ([prev, next]) => prev.report.updateRequired && !next.report.updateRequired && true === next.options.apply
        ),
        map(([_, next]) => next),
        shareReplay(1)
      );
    }

    return this.segmentsLoaded$;
  }

  // skips replayed value on subscription if state value changed
  public getNewLoadedSegments(): Observable<SegmentsState> {
    return combineLatest([this.getLoadedSegments(), this.store.pipe(select(getSegmentsReport))]).pipe(
      filter(([lastLoadedSegments, currentReportState]) => lastLoadedSegments.report === currentReportState),
      map(([lastLoadedSegments, _]) => lastLoadedSegments)
    );
  }

  private getSegmentsCombined(): Observable<SegmentsState> {
    if (!this.segmentsCombined$) {
      this.segmentsCombined$ = combineLatest([
        this.configService.getConfig(),
        this.store.pipe(select(getSegmentsOptions)),
        this.reportService.getReport()]
      ).pipe(
        map(([configState, optionsState, reportState]) => ({
          config: configState,
          options: optionsState,
          report: reportState
        })),
        catchError(error => {
          if (error && error.error !== undefined && (+error.error.code === 40 || +error.error.code === 42)) {
            return of(new CoreActions.DirectUserRoleHasBeenChanged());
          }
        })
      );
    }

    return this.segmentsCombined$;
  }
}
